import { Address } from '@synergy/models';
import { Box, IconButton, Tooltip } from '@mui/material';
import { CloseOutlined, SearchOutlined } from '@mui/icons-material';
import { ReactElement, ReactNode, useState } from 'react';
import { useIntl } from 'react-intl';

import { Contact } from '../../models/contact';
import { Customer } from '../../models/customer';
import { QuickSearch } from '../autocomplete-list/quickSearch/quickSearch';

export interface PaloCustomerQuickSearchProps {
    id: string;
    /** Whether a customer is picked; without one the quick search is the field. */
    picked: boolean;
    readonly?: boolean;
    onSelected: (customer: Customer, contact: Contact | undefined, address: Address) => void;
    /** One row per customer and address, the contact picked afterwards (R-225). */
    onePerAddress?: boolean;
    /** The customer field itself, handed the magnifier that brings the search back. */
    children: (searchButton: ReactNode) => ReactNode;
}

/**
 * The customer field with the quick search built in (#2251).
 *
 * A new document starts with the search, which finds customer, contact and
 * address in one go and fills all three. Once a customer is picked the field
 * shows it, and the magnifier at its end opens the search again — the search no
 * longer takes a row of its own.
 */
export const PaloCustomerQuickSearch = ({
    id,
    picked,
    readonly,
    onSelected,
    onePerAddress,
    children,
}: PaloCustomerQuickSearchProps): ReactElement => {
    const intl = useIntl();
    const [searching, setSearching] = useState(false);
    const label = intl.formatMessage({ id: 'app.common.quickSearch' });

    if (readonly || (picked && !searching)) {
        return (
            <>
                {children(
                    readonly ? null : (
                        <Tooltip key="quick-search" title={label}>
                            <IconButton size="small" aria-label={label} onClick={() => setSearching(true)}>
                                <SearchOutlined sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                    )
                )}
            </>
        );
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <QuickSearch
                    id={id}
                    inline
                    label={null}
                    placeholder={label}
                    autoFocus={searching}
                    onePerAddress={onePerAddress}
                    onCustomerSelected={(customer, contact, address) => {
                        setSearching(false);
                        onSelected(customer, contact, address);
                    }}
                />
            </Box>
            {picked && (
                <IconButton
                    size="small"
                    aria-label={intl.formatMessage({ id: 'app.common.close' })}
                    onClick={() => setSearching(false)}
                >
                    <CloseOutlined sx={{ fontSize: 18 }} />
                </IconButton>
            )}
        </Box>
    );
};
